import { HTMLAttributes, memo, useState } from 'react';
import styled from '@emotion/styled';
import { Badge, Popover, Rate } from 'antd';
import detailSvg from 'views/Aide/assets/more.svg';
import CommentPopoverContentView from './CommentPopoverContentView';
import { useCommentRatingValue } from './useCommentsTranslationController';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  > div {
    flex: 1 0 0;
    margin: 0 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  > span {
    flex: 0 0 auto;
  }
`;

const StarRate = styled(Rate)`
  font-size: 14px;
  line-height: 28px;
  > li {
    margin-right: 3px !important;
  }
`;

const Trigger = styled.span<{ visible?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 2px;
  cursor: pointer;
`;

const DetailImg = styled.img`
  width: 16px;
  height: 16px;
`;

export interface TradeCommentViewProps extends HTMLAttributes<HTMLDivElement> {
  comments: Conversation.Trade.Comment[];
  platform: string;
  previewText: string;
  needTranslate: boolean;
  displayTranslation: boolean;
  translations: Array<string | undefined>;
  translating: boolean;
  translationError: unknown;
  onToggleTranslation: () => void;
  showPreviewText?: boolean;
}

function TradeCommentView(props: TradeCommentViewProps) {
  const {
    comments,
    platform,
    previewText,
    needTranslate,
    displayTranslation,
    translations,
    translating,
    translationError,
    onToggleTranslation,
    showPreviewText = true,
    ...otherProps
  } = props;

  const [visible, setVisible] = useState(false);

  const { ratingValue } = useCommentRatingValue(comments, platform);

  const popoverContent = (
    <CommentPopoverContentView
      comments={comments}
      platform={platform}
      needTranslate={needTranslate}
      displayTranslation={displayTranslation}
      translations={translations}
      translating={translating}
      translationError={translationError}
      onToggleTranslation={onToggleTranslation}
    />
  );

  return (
    <Container {...otherProps}>
      <StarRate disabled allowHalf count={5} value={ratingValue} />
      {showPreviewText && <div title={previewText}>{previewText}</div>}
      <Popover
        visible={visible}
        onVisibleChange={setVisible}
        trigger="click"
        arrowPointAtCenter
        placement="bottomRight"
        content={popoverContent}
      >
        <Trigger visible={String(visible)} onClick={(e) => e.stopPropagation()}>
          <Badge dot={comments.length > 1}>
            <DetailImg src={detailSvg} alt="detail" />
          </Badge>
        </Trigger>
      </Popover>
    </Container>
  );
}

export default memo(TradeCommentView);
